import React, { useEffect, useState } from 'react';
import { Plus, Trash2, RefreshCw, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  listFristConfigs,
  createFristConfig,
  updateFristConfig,
  deleteFristConfig,
  type FristConfig,
  FRIST_TYPE_LABELS,
} from '../../../lib/kuendigungen';
import { masterdataApi, type UnterrichtsformItem } from '../../../lib/courses';
import { useStandort } from '../../../components/layouts/use-standort';
import { toast } from 'sonner';

const KuendigungsfristenSettings: React.FC = () => {
  const { standorte } = useStandort();
  const [configs, setConfigs] = useState<FristConfig[]>([]);
  const [unterrichtsformen, setUnterrichtsformen] = useState<UnterrichtsformItem[]>([]);
  const [loading, setLoading] = useState(false);

  // New config form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStandortId, setNewStandortId] = useState<string>('');
  const [newUnterrichtsformId, setNewUnterrichtsformId] = useState<string>('');
  const [newFristType, setNewFristType] = useState<string>('days_fixed');
  const [newFristValue, setNewFristValue] = useState<string>('28');
  const [newMindestlaufzeit, setNewMindestlaufzeit] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, u] = await Promise.all([
        listFristConfigs(),
        masterdataApi.unterrichtsformen(),
      ]);
      setConfigs(c);
      setUnterrichtsformen(u);
    } catch {
      toast.error('Konfigurationen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newFristValue || Number(newFristValue) <= 0) {
      toast.error('Bitte geben Sie einen gültigen Fristwert ein');
      return;
    }
    setSaving(true);
    try {
      const created = await createFristConfig({
        standortId: newStandortId ? Number(newStandortId) : null,
        unterrichtsformId: newUnterrichtsformId ? Number(newUnterrichtsformId) : null,
        fristType: newFristType as FristConfig['fristType'],
        fristValue: Number(newFristValue),
        mindestlaufzeitMonths: newMindestlaufzeit ? Number(newMindestlaufzeit) : null,
        aktiv: true,
      });
      setConfigs(prev => [...prev, created]);
      setShowAddForm(false);
      resetForm();
      toast.success('Kündigungsfrist-Konfiguration erstellt');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: number, data: Partial<FristConfig>) => {
    try {
      const updated = await updateFristConfig(id, data);
      setConfigs(prev => prev.map(c => c.id === id ? updated : c));
      toast.success('Konfiguration aktualisiert');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Konfiguration wirklich löschen?')) return;
    try {
      await deleteFristConfig(id);
      setConfigs(prev => prev.filter(c => c.id !== id));
      toast.success('Konfiguration gelöscht');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Fehler beim Löschen');
    }
  };

  const resetForm = () => {
    setNewStandortId('');
    setNewUnterrichtsformId('');
    setNewFristType('days_fixed');
    setNewFristValue('28');
    setNewMindestlaufzeit('');
  };

  const fristLabel = (type: string, value: number): string => {
    switch (type) {
      case 'days_fixed':
        return `${value} Tage`;
      case 'zum_monatsende':
        return `Zum Monatsende (${value} Monat${value !== 1 ? 'e' : ''} Vorlauf)`;
      case 'zum_quartalsende':
        return `Zum Quartalsende (${value} Monat${value !== 1 ? 'e' : ''} Vorlauf)`;
      case 'zum_periodenende':
        return `Zum Ende des Beitragszeitraums (${value} Monat${value !== 1 ? 'e' : ''} Vorlauf)`;
      default:
        return `${value}`;
    }
  };

  const scopeLabel = (config: FristConfig): string => {
    const parts: string[] = [];
    if (config.standortName) parts.push(config.standortName);
    if (config.unterrichtsformName) parts.push(config.unterrichtsformName);
    return parts.length > 0 ? parts.join(' / ') : 'Global (alle Standorte & Formen)';
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Kündigungsfristen</h1>
        <p className="text-muted-foreground">
          Konfigurieren Sie Kündigungsfristen pro Standort und Unterrichtsform
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="mr-2 h-5 w-5" />
            Frist-Konfigurationen
          </CardTitle>
          <CardDescription>
            Spezifischere Regeln (Standort + Unterrichtsform) haben Vorrang vor allgemeineren.
            Ohne passende Konfiguration gilt ein Standard von 30 Tagen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Action bar */}
          <div className="flex gap-2">
            <Button onClick={() => setShowAddForm(true)} disabled={showAddForm}>
              <Plus className="mr-2 h-4 w-4" />Neue Regel
            </Button>
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" />Aktualisieren
            </Button>
          </div>

          {/* Add form */}
          {showAddForm && (
            <div className="rounded-lg border bg-muted/20 p-4 space-y-4">
              <div className="text-sm font-semibold">Neue Kündigungsfrist-Regel</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Standort</Label>
                  <Select value={newStandortId} onValueChange={setNewStandortId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Alle Standorte" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Alle Standorte</SelectItem>
                      {standorte.map(s => (
                        <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Unterrichtsform</Label>
                  <Select value={newUnterrichtsformId} onValueChange={setNewUnterrichtsformId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Alle Formen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Alle Formen</SelectItem>
                      {unterrichtsformen.map(u => (
                        <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Fristtyp</Label>
                  <Select value={newFristType} onValueChange={setNewFristType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(FRIST_TYPE_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    {newFristType === 'days_fixed' ? 'Tage' : 'Monate Vorlauf'}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    value={newFristValue}
                    onChange={e => setNewFristValue(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Mindestlaufzeit (Monate)</Label>
                  <Input
                    type="number"
                    min={0}
                    placeholder="Optional"
                    value={newMindestlaufzeit}
                    onChange={e => setNewMindestlaufzeit(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleAdd} disabled={saving}>
                  {saving ? 'Wird erstellt...' : 'Erstellen'}
                </Button>
                <Button variant="outline" onClick={() => { setShowAddForm(false); resetForm(); }}>
                  Abbrechen
                </Button>
              </div>
            </div>
          )}

          {/* Configs list */}
          <div className="divide-y rounded border">
            {configs.map(config => (
              <div key={config.id} className="flex flex-col gap-2 p-3 md:flex-row md:items-center md:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{scopeLabel(config)}</div>
                  <div className="text-xs text-muted-foreground">
                    {fristLabel(config.fristType, config.fristValue)}
                    {config.mindestlaufzeitMonths != null && config.mindestlaufzeitMonths > 0 && (
                      <span> · Mindestlaufzeit: {config.mindestlaufzeitMonths} Monate</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.aktiv}
                      onChange={e => handleUpdate(config.id, { aktiv: e.target.checked })}
                    />
                    aktiv
                  </label>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(config.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {configs.length === 0 && !loading && (
              <div className="p-4 text-sm text-muted-foreground text-center">
                Keine Kündigungsfrist-Regeln vorhanden. Es gilt der Standard von 30 Tagen.
              </div>
            )}
            {loading && (
              <div className="p-4 text-sm text-muted-foreground text-center">Lade...</div>
            )}
          </div>
        </CardContent>
        <CardFooter>
          <div className="text-xs text-muted-foreground">
            Priorität: Standort + Unterrichtsform → Nur Standort → Global.
            Ohne passende Regel gelten 30 Tage feste Frist.
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default KuendigungsfristenSettings;
